import { useState, useCallback, useEffect } from "react";
import { memoryStore, setItemImmediate } from "../utils/storage";
import { reciprocalOf } from "../utils/relationshipTypes";

const REL_KEY = "pension_relationships_v1";

function loadAll() {
  try {
    const raw = memoryStore.getItem(REL_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveAll(all) {
  try { setItemImmediate(REL_KEY, JSON.stringify(all)); } catch {}
}

/**
 * useClientRelationships — קשרי משפחה דו-כיווניים בין לקוחות.
 * רשומה תחת לקוח A: { id, targetId, type } פירושה "targetId הוא ה-<type> של A".
 * כל הוספה/מחיקה כותבת גם את הצד ההופכי תחת targetId.
 */
export function useClientRelationships(clientId) {
  const [allData, setAllData] = useState(() => loadAll());
  
  useEffect(() => {
    const onSync = () => setAllData(loadAll());
    window.addEventListener('pension-data-synced', onSync);
    return () => window.removeEventListener('pension-data-synced', onSync);
  }, []);

  const relations = (allData[clientId] ?? []).map(r => {
    const theirs = allData[r.targetId] ?? [];
    const reverseRel = theirs.find(tr => tr.targetId === clientId);
    return {
      ...r,
      theirPortalAccess: reverseRel?.portalAccess || false
    };
  });

  const addRelation = useCallback((targetId, type) => {
    if (!clientId || !targetId || targetId === clientId) return;
    const all = loadAll();
    const mine = all[clientId] ?? [];
    if (mine.some(r => r.targetId === targetId)) return; // כבר קשורים

    const baseId = `rel-${Date.now()}`;
    all[clientId] = [...mine, { id: baseId, targetId, type }];
    const theirs = all[targetId] ?? [];
    all[targetId] = [
      ...theirs.filter(r => r.targetId !== clientId),
      { id: `${baseId}-r`, targetId: clientId, type: reciprocalOf(type) },
    ];

    saveAll(all);
    setAllData(all);
  }, [clientId]);

  const removeRelation = useCallback((relationId) => {
    const all = loadAll();
    const mine = all[clientId] ?? [];
    const rel = mine.find(r => r.id === relationId);
    if (!rel) return;

    all[clientId] = mine.filter(r => r.id !== relationId);
    const theirs = all[rel.targetId] ?? [];
    all[rel.targetId] = theirs.filter(r => r.targetId !== clientId);

    saveAll(all);
    setAllData(all);
  }, [clientId]);

  const toggleMyAccess = useCallback((relationId, access) => {
    const all = loadAll();
    const mine = all[clientId] ?? [];
    const idx = mine.findIndex(r => r.id === relationId);
    if (idx === -1) return;

    mine[idx] = { ...mine[idx], portalAccess: access };
    all[clientId] = [...mine];

    saveAll(all);
    setAllData(all);
  }, [clientId]);

  const toggleTheirAccess = useCallback((relationId, access) => {
    const all = loadAll();
    const mine = all[clientId] ?? [];
    const rel = mine.find(r => r.id === relationId);
    if (!rel) return;

    const theirs = all[rel.targetId] ?? [];
    const theirIdx = theirs.findIndex(r => r.targetId === clientId);
    if (theirIdx !== -1) {
      theirs[theirIdx] = { ...theirs[theirIdx], portalAccess: access };
      all[rel.targetId] = [...theirs];
      saveAll(all);
      setAllData(all);
    }
  }, [clientId]);

  return { relations, addRelation, removeRelation, toggleMyAccess, toggleTheirAccess };
}
